import React, { useEffect, useState } from 'react'
import {
  OrganizationAddressesBlock,
  OrganizationBasicInfoBlock,
  OrganizationHierarchyBlock,
  AlertDialog, OrganizationAuditBlock
  , UrlsStack
} from 'components'
import { Box, CircularProgress, Stack, styled } from '@mui/material'
import {
  getOrgBasicInfo,
  getOrgHierarchy,
  type OrganizationHierarchy,
  type OrganizationBasicInformation

} from 'services'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionContext } from 'session'

export interface OrganizationIdProps {
  orgId: number
}
const CenteredCircularProgress = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
})

export const OrganizationUpdatePage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const parsedOrgId = orgId != null ? parseInt(orgId, 10) : 1
  const [loading, setLoading] = useState(true)
  const [openErrorDialog, setOpenErrorDialog] = React.useState(false)
  const [errorMessage, seterrorMessage] = useState('')
  const [organizationHierarchy, setOrganizationHierarchy] = useState<OrganizationHierarchy>()
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const navigate = useNavigate()
  const [orgBasicInfo, setOrgBasicInfo] = useState <OrganizationBasicInformation>({
    id: 0,
    uniqueId: '',
    country: {
      id: 0,
      country_desc: '',
      country_code: '',
      states: []
    },
    orgTypeId: 0,
    organizationName: '',
    organizationNameLocal: '',
    parentOrganization: { id: 0, title: '', countryId: 0 },
    urls: [],
    lastUpdated: '',
    lastAudited: ''
  })

  useEffect(() => {
    changeModuleName('Organization')
  }, [changeModuleName])

  useEffect(() => {
    if (orgBasicInfo.uniqueId !== '') {
      changeUniqueId(`${parsedOrgId} - ${orgBasicInfo.uniqueId}`)
      document.title = `Org ${parsedOrgId} - ${orgBasicInfo.uniqueId ?? ''} - ${orgBasicInfo.organizationName ?? ''}`
    }
  }, [orgBasicInfo.uniqueId])

  const updateBasicInfo = (newData: OrganizationBasicInformation): void => {
    setOrgBasicInfo(newData)
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true)
        const orgBasicInfoResponse = await getOrgBasicInfo(parsedOrgId)
        setOrgBasicInfo(orgBasicInfoResponse)
      } catch (error: any) {
        if (error.response?.status === 404 || error.response?.status === 400) {
          seterrorMessage('Please verify the provided organization ID and try again. If you believe this is an error, or if you have any further questions, kindly contact our support team for assistance.')
        } else {
          seterrorMessage('We regret to inform you that an unexpected error has occurred during the request. Please ensure your internet connection is stable and attempt the action again. If the problem persists, feel free to reach out to our support team for prompt assistance. We sincerely appreciate your patience and cooperation.')
        }
        console.error(error)
        setOpenErrorDialog(true)
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
  }, [orgId])

  useEffect(() => {
    const fetchHierarchy = async (): Promise<void> => {
      try {
        const hierarchyResponse = await getOrgHierarchy(parsedOrgId)
        setOrganizationHierarchy(hierarchyResponse)
      } catch (error: any) {
        seterrorMessage('Error retrieving organization hierarchy')
        setOpenErrorDialog(true)
      }
    }
    void fetchHierarchy()
  }, [orgId, orgBasicInfo.parentOrganization?.id])

  const handleClose = (): void => {
    setOpenErrorDialog(false)
    navigate('/organizations')
  }

  const hasChildOrgs = (): boolean => {
    if (organizationHierarchy == null) {
      return false
    }
    return organizationHierarchy.lower_organizations.length > 0
  }
  const IsTopLevelOrg = (): boolean => {
    if (organizationHierarchy == null) {
      return false
    }
    return organizationHierarchy.upper_organizations.length === 0
  }

  return (
    <>
    {loading && <CenteredCircularProgress><CircularProgress size={100} /></CenteredCircularProgress>}
    {!loading && orgBasicInfo.id > 0 && (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 1
        }}
      >
        <Stack spacing={2} alignItems="center" mt={1} width="98%">
          <OrganizationAuditBlock
              originalBasicInfo={orgBasicInfo}
              loading={loading}
              updateLastAudited={lastAudited => { setOrgBasicInfo(prevState => ({ ...prevState, lastAudited })) }}
          />
          <OrganizationBasicInfoBlock
            originalBasicInfo={orgBasicInfo}
            loading={loading}
            updateBasicInfo={updateBasicInfo}
            isItAllowDeleteChild={!hasChildOrgs() && !IsTopLevelOrg()}
          />
          <OrganizationHierarchyBlock orgId={parsedOrgId} parentId={orgBasicInfo.parentOrganization?.id} hierarchy={organizationHierarchy} />
          <OrganizationAddressesBlock orgId={parsedOrgId}/>
          <UrlsStack orgId={parsedOrgId} personId={undefined}/>
        </Stack>
      </Box>
    )}
    <AlertDialog
        onClose={handleClose}
        open={openErrorDialog}
        onButtonClick={handleClose}
        title='Organization Not Found'
        contentText={errorMessage}
        nameButtonCancel='Close'
        dialogContentComponent={undefined}
      />
  </>
  )
}
